import React from "react";
import axios, { AxiosError } from "axios";
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Cookies, useCookies } from "react-cookie";
import { COOKIE_MAX_AGE, FE_BASE_URL, HTTP_TIMEOUT_MS } from "../utils/config";
import { persistor, store } from "../app/store";
import { storeUser, userSlice } from "../features/user/userSlice";
import { useNavigate } from "react-router-dom";
import {
  aziendaDiSessione,
  dimenticaAziendaDiSessione,
} from "../features/user/aziendaDiSessione";
import { goToPage } from "../utils/utils";

type Props = {
  children?: ReactNode;
};

type IAuthContext = {
  token: string | null;
  setToken: (newToken: string) => void;
};

const initAuth = {
  token: "",
  setToken: () => {},
};

const getCookieDomain = () => {
  const hostname = window.location.hostname;
  // Check if hostname is an IP address
  const isLocalhost = hostname === "localhost";
  //const isIpAddress = /^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/.test(hostname);

  let hn = isLocalhost
    ? undefined // Let browser handle localhost
    : //: isIpAddress
      hostname;
  //: `.${hostname.split(".").slice(-2).join(".")}`; // e.g. .example.com
  // console.log("cookie domain", hostname, hn);
  return hn;
};

const defaultCookieOption = {
  secure: window.location.protocol === "https:",
  maxAge: Number(COOKIE_MAX_AGE) * 24 * 60 * 60,
  path: "/",
  sameSite: "lax" as const,
  domain: getCookieDomain(),
};

/*
 Il timeout e' esplicito perche' il default di axios e' zero, cioe' nessun timeout: una richiesta
 che non torna mai lascia la promise pendente, il blocco finally non viene mai eseguito e lo
 spinner resta acceso a tempo indeterminato. E' il sintomo del ticket Odoo 291.
 Le operazioni legittimamente lente passano un timeout proprio sulla singola chiamata.
*/
export const axiosInstance = axios.create({
  timeout: HTTP_TIMEOUT_MS,
});

// Token corrente, mantenuto a livello di modulo e aggiornato da AuthProvider.
// L'interceptor qui sotto viene registrato una volta sola all'import: prima era
// registrato nel corpo del render di AuthProvider, quindi ogni re-render ne
// aggiungeva uno nuovo senza rimuovere i precedenti e la catena cresceva a
// ogni render, con tutti gli interceptor accumulati eseguiti su ogni richiesta.
let currentToken: string | null = null;

axiosInstance.interceptors.request.use(
  async (config) => {
    if (currentToken) {
      config.headers.Authorization = `Bearer ${currentToken}`;
    }
    /*
     L'azienda su cui lavora questa scheda, dichiarata a ogni richiesta: e' un dato della
     sessione, non dell'utenza. Vedi `src/features/user/aziendaDiSessione.ts`.

     Non e' un di piu' informativo: dal settembre 2026 e' **la** fonte dell'azienda di una
     richiesta (ADR-0017 del backend). Una chiamata che non passa di qui non la dichiara, e il
     backend ricade in silenzio sul claim del token: stessa pagina, dati di un'altra azienda.
     E' il motivo per cui il client e' uno solo - vedi la regola in `eslint.config.mjs`.
    */
    const azienda = aziendaDiSessione();
    if (azienda) {
      config.headers["X-Company-Id"] = azienda.id;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/*
 Il rifiuto dell'azienda fuori perimetro, e l'unica reazione che lo risolve.

 L'azienda dichiarata nell'intestazione vale solo dopo che il backend l'ha verificata contro il
 perimetro dell'utenza (ADR-0017 del backend). Se la scheda ne dichiara una che l'utenza non
 puo' piu' vedere - un superadmin declassato, un'azienda cancellata, permessi cambiati - la
 richiesta viene **rifiutata con 403** e non prosegue: nessuna pagina di questa sessione tornera'
 a funzionare finche' quella dichiarazione resta.

 E' l'unico 403 che va trattato qui. Tutti gli altri sono rifiuti applicativi legittimi - non hai
 il ruolo, non e' roba tua - che la pagina che li ha chiesti sa gia' spiegare, e buttar fuori
 dall'applicazione chi ha solo chiesto qualcosa che non gli spetta sarebbe peggio del rifiuto.
 Si distinguono dal messaggio, che il backend scrive in un punto solo, e dalla rotta: su
 `/auth/**` il backend questo rifiuto non lo applica di proposito, per non chiudere la via di
 rientro, quindi un 403 che arrivasse di li' non e' questo.
*/
const AZIENDA_FUORI_PERIMETRO = "Azienda non disponibile per questa utenza";

function eAziendaFuoriPerimetro(errore: unknown): boolean {
  const risposta = (errore as AxiosError<{ message?: string }> | undefined)
    ?.response;
  if (!risposta || risposta.status !== 403) {
    return false;
  }
  if ((risposta.config?.url ?? "").includes("/auth/")) {
    return false;
  }
  return risposta.data?.message === AZIENDA_FUORI_PERIMETRO;
}

/*
 Riporta alla scelta dell'azienda, che e' l'unica azione che toglie l'operatore da li'.

 Nell'ordine contano due cose. La prima: si dimentica l'azienda della scheda, altrimenti la
 pagina di arrivo la dichiarerebbe di nuovo e si rientrerebbe nello stesso 403, in cerchio. La
 seconda: via anche il token, perche' la scelta dell'azienda esiste solo dentro il login
 (`src/components/form/login`), e con un token valido la pagina di login rimanda dritti alla
 dashboard - cioe' all'azienda del token, un'altra, mostrata senza dirlo. Un nuovo accesso e'
 il solo modo di farsi rioffrire le aziende che l'utenza ha davvero adesso.
*/
function tornaAllaSceltaAzienda() {
  // Una pagina lancia piu' richieste insieme, e rifiutate tutte chiederebbero tutte la stessa
  // uscita: chi arriva dopo la trova gia' fatta, perche' l'azienda non c'e' piu'.
  if (!aziendaDiSessione()) {
    return;
  }
  dimenticaAziendaDiSessione();
  new Cookies().remove("token", {
    path: defaultCookieOption.path,
    domain: defaultCookieOption.domain,
  });
  goToPage("login");
}

/*
 La sessione finita: un 401 vuol dire che il backend non riconosce piu' il token - scaduto, non
 valido, utenza cancellata - e nessuna pagina tornera' a funzionare finche' lo si manda.

 Senza questa uscita l'utente restava chiuso dentro: il cookie c'era ancora, quindi il login lo
 rimandava alla dashboard, la dashboard prendeva 401 su ogni chiamata e mostrava tutti zeri, e
 l'unico rimedio era cancellare i cookie a mano. A differenza del 403 qui non c'e' da
 distinguere: il backend il 401 lo usa solo per dire «non sei autenticato», i rifiuti per ruolo
 o per azienda sono 403 (`UnauthorizedException` del backend).

 Tranne su `/auth/**`: li' un 401 e' la risposta attesa di chi sta cercando di entrare - la
 password sbagliata di `/auth/signin`, il token morto che `/auth/revalidate` scopre - e a
 gestirlo e' il login stesso. Reagirvi qui farebbe ricaricare la pagina di login a ogni
 tentativo sbagliato, o rimbalzare in cerchio su una revalidate che fallisce.
*/
function eSessioneScaduta(errore: unknown): boolean {
  const risposta = (errore as AxiosError | undefined)?.response;
  if (!risposta || risposta.status !== 401) {
    return false;
  }
  return !(risposta.config?.url ?? "").includes("/auth/");
}

// Una pagina lancia piu' richieste insieme, e con il token morto tornano tutte 401: la prima
// chiude la sessione, le altre la trovano gia' chiusa. Non serve riaprirla: si esce con un
// caricamento completo della pagina, che riparte da un modulo nuovo.
let sessioneInChiusura = false;

/*
 Chiude la sessione come fa il logout del menu utente, poi porta al login.

 Via il cookie, perche' e' da li' che un nuovo caricamento rileggerebbe il token morto e si
 ricomincerebbe da capo. Via anche l'utente in Redux: e' persistito in `sessionStorage`
 (`src/app/store.ts`), e con il nome ancora li' il login e la rotta protetta danno la sessione
 per verificata e saltano la revalidate, anche col prossimo token. Il `flush` aspetta che
 quella scrittura arrivi davvero in `sessionStorage` prima di lasciare la pagina, altrimenti la
 navigazione potrebbe precederla.
*/
function chiudiLaSessione() {
  if (sessioneInChiusura) {
    return;
  }
  sessioneInChiusura = true;
  new Cookies().remove("token", {
    path: defaultCookieOption.path,
    domain: defaultCookieOption.domain,
  });
  dimenticaAziendaDiSessione();
  store.dispatch(storeUser(userSlice.getInitialState()));
  persistor.flush().finally(() => goToPage("login"));
}

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (eAziendaFuoriPerimetro(error)) {
      tornaAllaSceltaAzienda();
    } else if (eSessioneScaduta(error)) {
      chiudiLaSessione();
    }
    // L'errore prosegue comunque verso chi ha fatto la chiamata: qui si aggiunge l'uscita, non
    // si nasconde il fallimento.
    return Promise.reject(error);
  }
);

const AuthContext = createContext<IAuthContext>(initAuth);

const AuthProvider = ({ children }: Props) => {
  const navigate = useNavigate();
  const [cookies, setCookie, removeCookie] = useCookies(["token"]);
  // State to hold the authentication token
  const [token, setToken_] = useState(cookies["token"]);
  // L'ultimo token scritto nel cookie: all'avvio e' quello appena letto dal cookie stesso.
  const tokenNelCookie = useRef<string | null | undefined>(cookies["token"]);

  // Tengo allineato il token letto dall'interceptor. L'assegnazione sta nel
  // corpo del render, non in un effect, perche' gli effect dei figli vengono
  // eseguiti prima di quelli del padre: le fetch lanciate al mount partirebbero
  // senza header Authorization.
  currentToken = token ?? null;

  // Function to set the authentication token
  const setToken = (newToken: string) => {
    try {
      //console.log("AuthProvider setToken", newToken);
      if (newToken === "logout") {
        setToken_(null);
      } else {
        setToken_(newToken);
      }
    } catch (error) {
      //console.log("Error setting token", error);
    }
  };

  /*
   Il cookie si scrive solo quando il token cambia davvero, e non deve sopravvivergli.

   Prima si riscriveva a ogni montaggio e a ogni cambio dell'utente in Redux, e ogni scrittura
   ne rinnovava il `maxAge`: il JWT invece scade 24 ore dopo essere stato emesso, e nessuno lo
   rinnovava. Bastava ricaricare la pagina per avere un cookie che durava piu' del token dentro,
   cioe' un browser che si credeva autenticato con un token che il backend rifiutava. Ora la
   durata del cookie parte quando parte quella del token (login, scelta dell'azienda,
   revalidate), e le due scadono insieme.
  */
  useEffect(() => {
    if (token === tokenNelCookie.current) {
      return;
    }
    tokenNelCookie.current = token;
    if (token) {
      setCookie("token", token, defaultCookieOption);
    } else {
      removeCookie("token", { ...defaultCookieOption, maxAge: -1 });
    }
  }, [removeCookie, setCookie, token]);

  // Memoized value of the authentication context
  const contextValue = useMemo(
    () => ({
      token,
      setToken,
    }),
    [token]
  );

  // Provide the authentication context to the children components
  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};

export default AuthProvider;
